import styled, { keyframes } from "styled-components";

const thinkingPulse = keyframes`
    0%, 80%, 100% {
        opacity: 0.35;
        transform: translateY(0);
    }

    40% {
        opacity: 1;
        transform: translateY(-2px);
    }
`;

export const ChatPage = styled.div`
    min-height: calc(100vh - 132px);
    margin: -8px -8px 0;
    background:
        radial-gradient(circle at 50% 22%, rgba(14, 165, 233, 0.13), transparent 28%),
        linear-gradient(180deg, #fbfdff 0%, #f2f6fb 100%);
    border-top: 1px solid #e7edf5;
`;

export const ChatShell = styled.div`
    min-height: calc(100vh - 132px);
    width: min(1040px, calc(100% - 48px));
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    padding: 28px 0 24px;

    @media only screen and (max-width: 640px) {
        width: calc(100% - 24px);
        padding: 18px 0 16px;
    }
`;

export const EmptyState = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 72px 16px 96px;
`;

export const ProChatMark = styled.div`
    width: 72px;
    height: 72px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    margin-bottom: 8px;
    background:
        conic-gradient(from 40deg, #2563eb, #7c3aed, #06b6d4, #2563eb),
        #ffffff;
    box-shadow: 0 12px 36px rgba(37, 99, 235, 0.22);
    position: relative;

    &::before {
        content: "";
        position: absolute;
        inset: 7px;
        border-radius: 50%;
        background: #f8fbff;
    }

    &::after {
        content: "";
        position: absolute;
        inset: 13px;
        border-radius: 50%;
        border: 2px solid rgba(37, 99, 235, 0.18);
    }
`;

export const WelcomeTitle = styled.h1`
    margin: 0;
    font-size: clamp(24px, 3.4vw, 34px);
    font-weight: 700;
    letter-spacing: 0;
    color: #111827;
    text-align: center;
    line-height: 1.15;
`;

export const WelcomeSubtitle = styled.p`
    max-width: 460px;
    margin: 0;
    color: #667085;
    font-size: 16px;
    line-height: 1.5;
    text-align: center;
`;

export const ProChatCore = styled.div`
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: #38bdf8;
    border: 8px solid #ffffff;
    position: relative;
    z-index: 1;
`;

export const Messages = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 6px 0 0;
`;

export const MessagesEnd = styled.div`
    flex: 0 0 118px;
    scroll-margin-bottom: 118px;

    @media only screen and (max-width: 640px) {
        flex-basis: 104px;
        scroll-margin-bottom: 104px;
    }
`;

export const MessageRow = styled.div<{ role: "user" | "assistant" | "system" | "error" }>`
    display: flex;
    justify-content: ${props => props.role === "user" ? "flex-end" : "flex-start"};
`;

export const MessageBubble = styled.div<{ role: "user" | "assistant" | "system" | "error" }>`
    max-width: ${props => props.role === "assistant" ? "min(860px, 100%)" : "min(720px, 86%)"};
    border-radius: 8px;
    padding: ${props => props.role === "assistant" ? "0" : "8px 12px"};
    line-height: 1.45;
    font-size: 15px;
    background: ${props => props.role === "user" ? "#2563eb" : props.role === "error" ? "#fff1f0" : "transparent"};
    color: ${props => props.role === "user" ? "#ffffff" : props.role === "error" ? "#a8071a" : "#1f2937"};
    border: 1px solid ${props => props.role === "user" ? "#2563eb" : props.role === "error" ? "#ffa39e" : "transparent"};
    box-shadow: ${props => props.role === "user" ? "0 8px 22px rgba(37, 99, 235, 0.16)" : "none"};

    @media only screen and (max-width: 640px) {
        max-width: ${props => props.role === "assistant" ? "100%" : "92%"};
    }
`;

export const MessageContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;

    p {
        margin: 0;
    }

    strong {
        font-weight: 600;
    }
`;

export const ListLine = styled.div`
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    gap: 8px;
    align-items: start;
`;

export const ThinkingDots = styled.span`
    color: #667085;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-height: 34px;

    &::after {
        content: "";
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background: currentColor;
        box-shadow: 8px 0 0 currentColor, 16px 0 0 currentColor;
        animation: ${thinkingPulse} 1.15s ease-in-out infinite;
        margin-left: 3px;
    }
`;

export const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
`;

export const Composer = styled.div`
    position: sticky;
    bottom: 0;
    padding: 14px 0 0;
    background: linear-gradient(180deg, rgba(242, 246, 251, 0), #f2f6fb 28%);
`;

export const ComposerInner = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) 36px 52px;
    gap: 8px;
    align-items: center;
    width: min(860px, 100%);
    min-height: 72px;
    margin: 0 auto;
    border: 1px solid #cfdbea;
    background: #ffffff;
    border-radius: 28px;
    padding: 10px 14px 10px 24px;
    box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.8),
        0 18px 48px rgba(15, 23, 42, 0.12);
    transition: border-color 160ms ease, box-shadow 160ms ease, background 160ms ease;

    &:focus-within {
        border-color: #60a5fa;
        background: #ffffff;
        box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.9),
            0 0 0 4px rgba(96, 165, 250, 0.15),
            0 22px 58px rgba(15, 23, 42, 0.14);
    }

    textarea.ant-input,
    textarea.ant-input:hover,
    textarea.ant-input:focus,
    textarea.ant-input-focused,
    textarea.ant-input[disabled] {
        font-size: 16px;
        line-height: 1.5;
        color: #111827;
        background: transparent !important;
        border: 0;
        box-shadow: none !important;
        outline: none;
        resize: none;
        padding: 0;
        -webkit-text-fill-color: #111827;
    }

    textarea.ant-input::placeholder {
        color: #8a94a6;
    }

    .composer-settings-button {
        width: 34px;
        height: 34px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        align-self: center;
        background: transparent;
        border-color: transparent;
        color: #64748b;
        box-shadow: none;
        font-size: 17px;
    }

    .composer-settings-button:hover,
    .composer-settings-button:focus {
        background: #eef4ff;
        border-color: transparent;
        color: #2563eb;
    }

    .composer-send-button {
        width: 48px;
        height: 48px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: #0f172a;
        border-color: #0f172a;
        color: #ffffff;
        box-shadow: none;
        font-size: 20px;
    }

    .composer-send-button:hover,
    .composer-send-button:focus {
        background: #2563eb;
        border-color: #2563eb;
        color: #ffffff;
    }

    .composer-send-button[disabled],
    .composer-send-button[disabled]:hover {
        background: #475569;
        border-color: #475569;
        color: #cbd5e1;
    }

    @media only screen and (max-width: 640px) {
        grid-template-columns: minmax(0, 1fr) 34px 44px;
        min-height: 62px;
        border-radius: 22px;
        padding: 8px 10px 8px 16px;

        .composer-settings-button {
            width: 32px;
            height: 32px;
            font-size: 15px;
        }

        .composer-send-button {
            width: 42px;
            height: 42px;
            font-size: 16px;
        }
    }
`;

export const ComposerMeta = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 6px 12px;
    margin-top: 12px;
    color: #7b8494;
    font-size: 12px;
    line-height: 1.4;
    text-align: center;

    span:first-child {
        color: #536075;
    }
`;

export const PreferencePanel = styled.div`
    width: 260px;
`;

export const PreferenceTitle = styled.div`
    margin-bottom: 12px;
    color: #111827;
    font-size: 14px;
    font-weight: 600;
`;

export const PreferenceRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    color: #1f2937;
    font-size: 14px;
`;

export const PreferenceHelp = styled.div`
    margin-top: 8px;
    color: #667085;
    font-size: 12px;
    line-height: 1.4;
`;
